import { DeviceHandle } from '../types'

/** iMin terminals' built-in printer (Android WebView with the iMin JS SDK). */
interface IminPrinter {
  initPrinter: () => Promise<unknown>
  sendRAWData: (bytes: number[]) => Promise<unknown>
}
declare global {
  interface Window {
    IminSDK?: { Printer: new () => IminPrinter }
  }
}

let imin: IminPrinter | null = null

export const iminPresent = () => typeof window !== 'undefined' && !!window.IminSDK

export const sendImin = async (data: Uint8Array) => {
  if (!window.IminSDK) throw new Error('The iMin printer is not available on this device')
  if (!imin) {
    imin = new window.IminSDK.Printer()
    await imin.initPrinter()
  }
  await imin.sendRAWData(Array.from(data))
}

const hex = (data: Uint8Array) => Array.from(data, (b) => b.toString(16).padStart(2, '0')).join('')

/**
 * LAN printers with an HTTP API (no bridge, POS.md §18.9 Q1). `epos`: Epson ePOS-Print — ESC/POS bytes wrapped as a
 * `<command>` element; the printer must allow the page's origin (CORS). `raw`: POST the bytes as they are.
 * Not verified on hardware yet.
 */
export const sendHttp = async (h: NonNullable<DeviceHandle['http']>, data: Uint8Array | string) => {
  const bytes = typeof data === 'string' ? new TextEncoder().encode(data) : data
  if (h.flavor === 'epos') {
    const body =
      '<?xml version="1.0" encoding="utf-8"?><s:Envelope xmlns:s="http://schemas.xmlsoap.org/soap/envelope/"><s:Body>' +
      `<epos-print xmlns="http://www.epson-pos.com/schemas/2011/03/epos-print"><command>${hex(bytes)}</command></epos-print>` +
      '</s:Body></s:Envelope>'
    const res = await fetch(h.url, { method: 'POST', headers: { 'Content-Type': 'text/xml; charset=utf-8' }, body })
    if (!res.ok) throw new Error(`Printer answered ${res.status}`)
    return
  }
  const res = await fetch(h.url, { method: 'POST', body: new Blob([bytes as BlobPart]) })
  if (!res.ok) throw new Error(`Printer answered ${res.status}`)
}

/**
 * The system print dialog with rendered HTML, in a hidden frame so only the document prints (not the page). The dialog
 * picks the printer; silent printing needs Chrome's kiosk mode.
 */
export const printHtml = (html: string) =>
  new Promise<void>((resolve, reject) => {
    const frame = document.createElement('iframe')
    frame.setAttribute('aria-hidden', 'true')
    frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden'
    document.body.appendChild(frame)
    const done = () => {
      setTimeout(() => frame.remove(), 1000)
      resolve()
    }
    frame.onload = () => {
      const win = frame.contentWindow
      if (!win) return reject(new Error('Could not open the print frame'))
      win.addEventListener('afterprint', done, { once: true })
      // Images (logo, QR) load before printing.
      setTimeout(() => {
        win.focus()
        win.print()
        // Some browsers fire no afterprint; the dialog is modal, so print() has returned once it closed.
        setTimeout(done, 500)
      }, 250)
    }
    frame.srcdoc = html
  })
